import { Children, type ReactNode, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import useHiddenItemsObserver from 'hooks/globals/useHiddenItemsObserver';
import type { ToTransientProps } from 'types/styled';
import DefaultToggleButton from './components/DefaultToggleButton';
import type { ToggleButtonProps } from './shared/types';

export type TruncatedGridProps = {
    children: ReactNode;
    cellWidth: number;
    cellHeight: number;
    ToggleButtonRenderer?: (
        props: Required<Pick<ToggleButtonProps, 'hiddenItemsCount' | 'onClick' | 'expanded'>>
    ) => ReactNode;
} & (
    | {
          visibleRows?: 1 | 2 | 3;
          visibleItems?: never;
      }
    | {
          visibleRows?: never;
          visibleItems?: number;
      }
);

type TransientGridProps = Required<
    Pick<ToTransientProps<TruncatedGridProps>, '$cellWidth' | '$cellHeight' | '$visibleRows'>
>;
type TransientToggleButtonItemProps = Required<Pick<ToTransientProps<TruncatedGridProps>, '$visibleRows'>>;

const heightStyle = css<TransientGridProps>`
    ${({ $cellHeight, $visibleRows, theme }) => {
        const gapNumber = Number.parseInt(theme.Spacing8 as string);

        if ($visibleRows === 1) {
            return $cellHeight * $visibleRows;
        }

        return $cellHeight * $visibleRows + ($visibleRows - 1) * gapNumber;
    }}`;

const GridList = styled.ul<TransientGridProps & { $setGridMaxHeight: boolean }>`
    display: grid;
    grid-template-columns: repeat(auto-fit, ${({ $cellWidth }) => $cellWidth}px);
    grid-auto-rows: ${({ $cellHeight }) => $cellHeight}px;
    gap: ${({ theme }) => theme.Spacing8};
    list-style: none;
    margin: 0;
    padding: 0;
    overflow: hidden;
    transition: max-height 300ms ease;
    max-height: ${({ $setGridMaxHeight }) => ($setGridMaxHeight ? css<TransientGridProps>`${heightStyle}px` : 'none')};
`;

const GridItem = styled.li``;

const ToggleButtonItem = styled(GridItem)<TransientToggleButtonItemProps & { $snapToGrid: boolean }>`
    grid-column: ${({ $snapToGrid }) => ($snapToGrid ? -2 : 'auto')} ;
    grid-row: ${({ $visibleRows, $snapToGrid }) => ($snapToGrid ? $visibleRows : 'auto')};
`;

function TruncatedGrid(props: TruncatedGridProps) {
    const {
        children,
        cellHeight,
        cellWidth,
        visibleRows = 1,
        visibleItems,
        ToggleButtonRenderer = DefaultToggleButton,
        ...restProps
    } = props;
    const ref = useRef(null);
    const childRefs = useRef<HTMLElement[]>([]);
    const hiddenItemsCount = useHiddenItemsObserver({ ref, childRefs });
    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = () => setExpanded((prev) => !prev);
    const [showToggleButton, setShowToggleButton] = useState(false);
    const toggleButtonSnapToGrid = !expanded;
    const setGridMaxHeight = !expanded;

    useEffect(() => {
        if (hiddenItemsCount > 0) {
            setShowToggleButton(true);
        }

        if (hiddenItemsCount === 0 && !expanded) {
            setShowToggleButton(false);
        }
    }, [hiddenItemsCount, setShowToggleButton, expanded]);

    return (
        <GridList
            $cellHeight={cellHeight}
            $cellWidth={cellWidth}
            $visibleRows={visibleRows}
            $setGridMaxHeight={setGridMaxHeight}
            ref={ref}
            {...restProps}
        >
            {Children.map(children, (child, index) => {
                const visibleItemsCount = Children.count(children) - hiddenItemsCount;

                function expandGridIfHidden() {
                    if (index + 1 > visibleItemsCount) {
                        toggleExpanded();
                    }
                }

                return (
                    <GridItem
                        ref={(node) => node && childRefs.current.push(node)}
                        onFocus={expandGridIfHidden}
                        hidden={visibleItems ? index + 1 > visibleItems && !expanded : false}
                    >
                        {child}
                    </GridItem>
                );
            })}

            {showToggleButton && (
                <ToggleButtonItem $snapToGrid={toggleButtonSnapToGrid} $visibleRows={visibleRows}>
                    <ToggleButtonRenderer
                        onClick={toggleExpanded}
                        hiddenItemsCount={hiddenItemsCount}
                        expanded={expanded}
                    />
                </ToggleButtonItem>
            )}
        </GridList>
    );
}

export default TruncatedGrid;
